import Jimp from "jimp";
import Potrace from "potrace";
import { closestColor, exactColor, rgbToHex } from "../helpers/helpers";
import process from "../helpers/runonnx";

const convertAlgorithm = "closest";

// --- Color space (sRGB -> Lab, ΔE76) ---
function rgb2lab(r, g, b) {
  const srgb = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.04045 ? v/12.92 : Math.pow((v+0.055)/1.055, 2.4);
  });
  const [R,G,B] = srgb;
  let X = (0.4124564*R + 0.3575761*G + 0.1804375*B) / 0.95047;
  let Y = (0.2126729*R + 0.7151522*G + 0.0721750*B);
  let Z = (0.0193339*R + 0.1191920*G + 0.9503041*B) / 1.08883;
  const f = t => t > 0.008856 ? Math.cbrt(t) : (7.787*t + 16/116);
  const fx = f(X), fy = f(Y), fz = f(Z);
  return {L: 116*fy-16, a: 500*(fx-fy), b: 200*(fy-fz)};
}

function deltaE(l1, l2) {
  const dL = l1.L-l2.L, da = l1.a-l2.a, db = l1.b-l2.b;
  return Math.hypot(dL, da, db);
}

// --- K-means in Lab on "superpixel" means (tiles) ---
function kMeansLab(points, k, maxIter=30) {
  const centroids = [];
  for (let i=0;i<k;i++) centroids.push(points[Math.floor(Math.random()*points.length)]);
  let changed = true, iter = 0;
  let assigns = new Array(points.length).fill(-1);

  while (changed && iter++ < maxIter) {
    changed = false;
    const sums = new Array(k).fill(0).map(()=>({L:0,a:0,b:0,c:0}));
    for (let i=0;i<points.length;i++) {
      let best=-1, bestD=1e9;
      for (let j=0;j<k;j++) {
        const d = deltaE(points[i], centroids[j]);
        if (d<bestD) {best=j; bestD=d;}
      }
      if (assigns[i]!==best) {assigns[i]=best; changed=true;}
      sums[best].L += points[i].L;
      sums[best].a += points[i].a;
      sums[best].b += points[i].b;
      sums[best].c++;
    }
    for (let j=0;j<k;j++) if (sums[j].c>0) {
      centroids[j] = {L:sums[j].L/sums[j].c, a:sums[j].a/sums[j].c, b:sums[j].b/sums[j].c};
    }
  }
  return {centroids, assigns};
}

// Merge close Lab colors
function mergeCloseLab(centroids, deltaEThresh=2.5) {
  const out = [];
  for (const c of centroids) {
    let merged = false;
    for (const m of out) {
      if (deltaE(c, m) < deltaEThresh) {
        m.L = (m.L + c.L)/2; m.a = (m.a + c.a)/2; m.b = (m.b + c.b)/2;
        merged = true; break;
      }
    }
    if (!merged) out.push({...c});
  }
  return out;
}

// Lab -> sRGB hex (for Potrace fill)
function labToHex(Lab) {
  // quick-and-clean Lab->sRGB (approx)
  const {L,a,b} = Lab;
  let fy = (L+16)/116, fx = fy + a/500, fz = fy - b/200;
  const invf = t => {
    const t3 = t*t*t;
    return t3>0.008856 ? t3 : (t-16/116)/7.787;
  };
  let X = 0.95047*invf(fx), Y = invf(fy), Z = 1.08883*invf(fz);
  let R =  3.2406*X + -1.5372*Y + -0.4986*Z;
  let G = -0.9689*X +  1.8758*Y +  0.0415*Z;
  let B =  0.0557*X + -0.2040*Y +  1.0570*Z;
  const enc = v => {
    v = Math.max(0, Math.min(1, v));
    return v <= 0.0031308 ? 12.92*v : 1.055*Math.pow(v,1/2.4)-0.055;
  };
  const r = Math.round(enc(R)*255), g=Math.round(enc(G)*255), bl=Math.round(enc(B)*255);
  const hx = n => n.toString(16).padStart(2,'0');
  return `#${hx(r)}${hx(g)}${hx(bl)}`;
}

// Edge-aware color quantization that prevents gaps at boundaries
function improvedColorQuantization(image, COLORS, numLayers) {
  const width = image.getWidth();
  const height = image.getHeight();
  const layers = new Array(numLayers);
  for (let i = 0; i < numLayers; i++) layers[i] = new Jimp(width, height, 0xffffffff);

  // Convert palette to Lab space
  const paletteLab = COLORS.map(([r,g,b]) => rgb2lab(r,g,b));

  // Create assignment map using Lab color distance
  const assignments = new Array(width * height);
  const edgePixels = new Array(width * height).fill(false);
  
  // First pass: Basic color assignment
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const rgba = Jimp.intToRGBA(image.getPixelColor(x, y));
      if (rgba.a < 25) {
        assignments[y * width + x] = -1; // transparent
        continue;
      }

      const pixelLab = rgb2lab(rgba.r, rgba.g, rgba.b);
      let best = -1, bestD = 1e9, secondBest = -1, secondBestD = 1e9;
      
      for (let i = 0; i < paletteLab.length; i++) {
        const d = deltaE(pixelLab, paletteLab[i]);
        if (d < bestD) {
          secondBest = best;
          secondBestD = bestD;
          best = i;
          bestD = d;
        } else if (d < secondBestD) {
          secondBest = i;
          secondBestD = d;
        }
      }
      
      assignments[y * width + x] = best;
      
      // Mark as edge pixel if the two closest colors are very close in distance
      // This indicates the pixel is on a boundary between color regions
      if (secondBest >= 0 && (secondBestD - bestD) < 5) { // Small threshold for edge detection
        edgePixels[y * width + x] = true;
      }
    }
  }

  // Second pass: Edge-aware reassignment to prevent gaps
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      if (!edgePixels[idx] || assignments[idx] === -1) continue;
      
      // For edge pixels, look at neighborhood to make better assignment
      const neighborColors = new Array(numLayers).fill(0);
      let totalNeighbors = 0;
      
      // Check 3x3 neighborhood
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nIdx = (y + dy) * width + (x + dx);
          const nColor = assignments[nIdx];
          if (nColor >= 0) {
            neighborColors[nColor]++;
            totalNeighbors++;
          }
        }
      }
      
      // If this edge pixel would create a gap (no strong neighborhood support),
      // assign it to the dominant neighbor color
      if (totalNeighbors > 0) {
        const currentColorCount = neighborColors[assignments[idx]];
        let maxCount = 0, dominantColor = assignments[idx];
        
        for (let i = 0; i < numLayers; i++) {
          if (neighborColors[i] > maxCount) {
            maxCount = neighborColors[i];
            dominantColor = i;
          }
        }
        
        // Reassign if current color has weak neighborhood support
        if (currentColorCount < totalNeighbors * 0.3 && maxCount > totalNeighbors * 0.4) {
          assignments[idx] = dominantColor;
        }
      }
    }
  }

  // Simple majority vote smoothing - no complex weighting
  const smoothed = assignments.slice();
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const centerIdx = y * width + x;
      if (assignments[centerIdx] === -1) continue;
      
      // Simple neighbor counting
      const counts = new Array(numLayers).fill(0);
      let totalCount = 0;
      
      // 3x3 neighborhood
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const idx = (y + dy) * width + (x + dx);
          const assignment = assignments[idx];
          if (assignment >= 0) {
            counts[assignment]++;
            totalCount++;
          }
        }
      }
      
      // Use majority rule, but be more aggressive about filling gaps
      if (totalCount > 0) {
        let maxCount = 0, bestColor = assignments[centerIdx];
        for (let i = 0; i < numLayers; i++) {
          if (counts[i] > maxCount) {
            maxCount = counts[i];
            bestColor = i;
          }
        }
        
        // If current color has less than 40% support, switch to majority
        if (counts[assignments[centerIdx]] < totalCount * 0.4) {
          smoothed[centerIdx] = bestColor;
        }
      }
    }
  }

  // Fill the layer masks
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const assignment = smoothed[y * width + x];
      if (assignment >= 0) {
        layers[assignment].setPixelColor(0x000000ff, x, y);
      }
    }
  }

  return layers;
}

// Simple binary morphology for Jimp mask layers (black=ink, white=bg)
function maskToBinary(layer) {
  const w = layer.getWidth(), h = layer.getHeight();
  const bin = new Uint8Array(w*h);
  for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
    const {r} = Jimp.intToRGBA(layer.getPixelColor(x,y));
    bin[y*w+x] = (r===0) ? 255 : 0;
  }
  return {bin, w, h};
}

function binaryToMask(bin, w, h, layer) {
  for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
    layer.setPixelColor(bin[y*w+x] ? 0x000000ff : 0xffffffff, x, y);
  }
}

function dilate3x3(bin, w, h) {
  const out = new Uint8Array(bin.length);
  for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
    let on = 0;
    for (let j=-1;j<=1;j++) for (let i=-1;i<=1;i++) {
      const xx=x+i, yy=y+j;
      if (xx>=0 && yy>=0 && xx<w && yy<h) on |= (bin[yy*w+xx]>0);
    }
    out[y*w+x] = on?255:0;
  }
  return out;
}

function erode3x3(bin, w, h) {
  const out = new Uint8Array(bin.length);
  for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
    let all=1;
    for (let j=-1;j<=1;j++) for (let i=-1;i<=1;i++) {
      const xx=x+i, yy=y+j;
      if (xx<0||yy<0||xx>=w||yy>=h || bin[yy*w+xx]===0) { all=0; break; }
    }
    out[y*w+x] = all?255:0;
  }
  return out;
}

function closeOpen(bin, w, h) {
  // More gentle morphology for illustrations
  const closed = erode3x3(dilate3x3(bin,w,h), w,h);
  const opened = dilate3x3(erode3x3(closed,w,h), w,h);
  
  // Additional smoothing pass to reduce jaggedness
  const smoothed = dilate3x3(opened, w, h);
  const refined = erode3x3(smoothed, w, h);
  
  return refined;
}

// Additional edge smoothing specifically for cleaner illustration edges
function smoothEdges(bin, w, h) {
  const out = bin.slice();
  
  // Apply a gentle smoothing kernel to reduce jaggedness
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const center = bin[y * w + x];
      
      // Count 8-connected neighbors
      let neighbors = 0;
      let total = 0;
      
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const val = bin[(y + dy) * w + (x + dx)];
          if (val > 0) neighbors++;
          total++;
        }
      }
      
      // Conservative gap filling to preserve shapes
      if (center === 0 && neighbors >= 7) {
        out[y * w + x] = 255; // Fill only very small gaps
      } else if (center > 0 && neighbors <= 1) {
        out[y * w + x] = 0; // Remove only truly isolated pixels
      }
    }
  }
  
  return out;
}

// Specialized function to close thin gaps and cracks
function closeThinGaps(bin, w, h) {
  const out = bin.slice();
  
  // Look for thin gaps (1-2 pixels wide) between solid areas
  for (let y = 2; y < h - 2; y++) {
    for (let x = 2; x < w - 2; x++) {
      if (bin[y * w + x] > 0) continue; // Skip if already filled
      
      // Check for horizontal gaps
      const left = bin[y * w + (x - 1)] > 0 || bin[y * w + (x - 2)] > 0;
      const right = bin[y * w + (x + 1)] > 0 || bin[y * w + (x + 2)] > 0;
      
      // Check for vertical gaps
      const top = bin[(y - 1) * w + x] > 0 || bin[(y - 2) * w + x] > 0;
      const bottom = bin[(y + 1) * w + x] > 0 || bin[(y + 2) * w + x] > 0;
      
      // Check for diagonal connections
      const topLeft = bin[(y - 1) * w + (x - 1)] > 0;
      const topRight = bin[(y - 1) * w + (x + 1)] > 0;
      const bottomLeft = bin[(y + 1) * w + (x - 1)] > 0;
      const bottomRight = bin[(y + 1) * w + (x + 1)] > 0;
      
      // Only fill very obvious gaps, be more conservative
      const strongHorizontalGap = (bin[y * w + (x - 1)] > 0 && bin[y * w + (x + 1)] > 0) && 
                                  (bin[(y - 1) * w + x] > 0 || bin[(y + 1) * w + x] > 0);
      const strongVerticalGap = (bin[(y - 1) * w + x] > 0 && bin[(y + 1) * w + x] > 0) && 
                                (bin[y * w + (x - 1)] > 0 || bin[y * w + (x + 1)] > 0);
      
      // Only fill if it's clearly a 1-pixel gap with strong evidence
      if (strongHorizontalGap || strongVerticalGap) {
        out[y * w + x] = 255;
      }
    }
  }
  
  return out;
}

function removeSmall(bin, w, h, minAreaPx) {
  const out = bin.slice();
  const seen = new Uint8Array(bin.length);
  const qx = new Int32Array(bin.length);
  const qy = new Int32Array(bin.length);

  for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
    const idx=y*w+x;
    if (!out[idx] || seen[idx]) continue;
    // BFS component
    let head=0, tail=0;
    seen[idx]=1; qx[tail]=x; qy[tail]=y; tail++;
    const pts=[];
    while(head<tail){
      const cx=qx[head], cy=qy[head]; head++;
      pts.push([cx,cy]);
      const nbs=[[1,0],[-1,0],[0,1],[0,-1]];
      for (const [dx,dy] of nbs){
        const nx=cx+dx, ny=cy+dy;
        if (nx<0||ny<0||nx>=w||ny>=h) continue;
        const nidx=ny*w+nx;
        if (out[nidx] && !seen[nidx]) { seen[nidx]=1; qx[tail]=nx; qy[tail]=ny; tail++; }
      }
    }
    if (pts.length < minAreaPx) {
      for (const [px,py] of pts) out[py*w+px]=0;
    }
  }
  return out;
}

const processJimpImage = async (imageData, COLORS, numLayers, options) => {
  const { despeckle, simplifyCurves, smoothness, colors, originalDimensions, useSuperpixels = true, useMorphology = false } =
    options;
  console.log("Processing image with Jimp", imageData);
  console.log("useSuperpixels:", useSuperpixels);
  console.log("Color palette being used:", COLORS);
  const image = await Jimp.read(imageData);
  const height = image.getHeight();
  const width = image.getWidth();

  let colorLayers;
  
  if (!useSuperpixels) {
    // Use original per-pixel processing for fine detail
    console.log("Using legacy per-pixel algorithm with RGB matching");
    colorLayers = new Array(numLayers);
    for (let i = 0; i < numLayers; i++) {
      colorLayers[i] = new Jimp(width, height, 0xffffffff);
    }

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const rgba = Jimp.intToRGBA(image.getPixelColor(x, y));
        if (rgba.a < 25) continue;

        let pixelColorRGB;
        if (convertAlgorithm === "closest") {
          pixelColorRGB = closestColor(
            image.getPixelColor(x, y),
            COLORS,
            numLayers
          );
        } else if (convertAlgorithm === "exact") {
          pixelColorRGB = exactColor(
            image.getPixelColor(x, y),
            COLORS,
            numLayers
          );
          if (pixelColorRGB === false) continue;
        }

        for (let color_i = 0; color_i < colorLayers.length; color_i++) {
          if (
            pixelColorRGB.r === COLORS[color_i][0] &&
            pixelColorRGB.g === COLORS[color_i][1] &&
            pixelColorRGB.b === COLORS[color_i][2]
          ) {
            colorLayers[color_i].setPixelColor(0x000000ff, x, y);
          }
        }
      }
    }
  } else {
    console.log("Using superpixel algorithm with Lab color space");
    colorLayers = improvedColorQuantization(image, COLORS, numLayers);
  }

  if (useMorphology) {
    for (let i = 0; i < numLayers; i++) {
      const {bin, w, h} = maskToBinary(colorLayers[i]);
      const minArea = Math.max(3, Math.floor(0.000005 * w * h));
      let processed = removeSmall(bin, w, h, minArea);
      processed = dilate3x3(processed, w, h);
      processed = erode3x3(processed, w, h);
      binaryToMask(processed, w, h, colorLayers[i]);
    }
  }

  const traces = Array(numLayers);
  for (let i = 0; i < numLayers; i++) {
    traces[i] = new Potrace.Potrace();
    traces[i].setParameters({
      color: rgbToHex(COLORS[i][0], COLORS[i][1], COLORS[i][2]),
      turdSize: despeckle,
      optTolerance: simplifyCurves,
      alphaMax: smoothness,
    });
  }

  const promises = colorLayers.map(
    (layer, i) =>
      new Promise((resolve) => {
        layer.getBase64(Jimp.MIME_PNG, (err, src) => {
          traces[i].loadImage(src, () => resolve());
        });
      })
  );

  await Promise.all(promises);

  const svgHead = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" version="1.1">`;
  const svgTail = "</svg>";

  let allPaths = "";

  for (let index = 0; index < colors.length; index++) {
    allPaths += traces[index].getPathTag();
  }

  const newSVG = svgHead + allPaths + svgTail;
  const svgPreview = "data:image/svg+xml;base64," + window.btoa(newSVG);

  const containerWidth = window.innerWidth - 50;
  const containerHeight = (2 * window.innerHeight) / 3;

  let scaledImageHeight = originalDimensions[1];
  let scaledImageWidth = originalDimensions[0];

  let scale = Math.min(
    containerWidth / originalDimensions[0],
    containerHeight / originalDimensions[1]
  );

  if (scale < 1) {
    scaledImageHeight = Math.floor(originalDimensions[1] * scale);
    scaledImageWidth = Math.floor(originalDimensions[0] * scale);
  }

  const blob = new Blob([newSVG], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);

  return {
    svg: newSVG,
    svgPreview,
    downloadSVGLink: url,
    scaledImageHeight,
    scaledImageWidth,
  };
};


const evaluateHardwareCapabilities = () => {
  const memory = navigator?.deviceMemory || 4;
  const cpuCores = navigator?.hardwareConcurrency || 4;

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  const isCapacitor = !!(window.Capacitor?.isNativePlatform?.());
  const isIPad = /iPad/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  let maxPixels;
  let deviceType;

  if (isCapacitor || isIPad) {
    maxPixels = 1440000;
    deviceType = "ios-native";
  } else if (memory >= 8 && cpuCores >= 8 && !isMobile) {
    maxPixels = 4000000;
    deviceType = "high-end";
  } else if (memory <= 2 || cpuCores <= 2 || isMobile) {
    maxPixels = 1500000;
    deviceType = "low-end";
  } else {
    maxPixels = 2500000;
    deviceType = "medium";
  }

  return {
    maxPixels,
    memory,
    cpuCores,
    isMobile,
    isCapacitor,
    isIPad,
    deviceType,
  };
};

export const processImage = async (params) => {
  const {
    colors,
    file,
    crop,
    despeckle,
    simplifyCurves,
    smoothness,
    ai,
    originalDimensions,
    useSuperpixels = true,
    useMorphology = false,
  } = params;

  const hardware = evaluateHardwareCapabilities();

  let numLayers = colors.length;
  let COLORS = colors.map((color) => {
    const rgba = Jimp.intToRGBA(parseInt("0x" + color[0] + "00"));
    return [rgba.r, rgba.g, rgba.b];
  });

  const img = new Image();
  img.src = file;

  return new Promise((resolve, reject) => {
    img.onload = async () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      let cropWidth = crop.width;
      let cropHeight = crop.height;

      const MAX_PIXELS = hardware.maxPixels;
      if (cropWidth * cropHeight > MAX_PIXELS) {
        const scale = Math.sqrt(MAX_PIXELS / (cropWidth * cropHeight));
        cropWidth = Math.floor(cropWidth * scale);
        cropHeight = Math.floor(cropHeight * scale);
      }

      const SIZE_LIMIT = (hardware.isCapacitor || hardware.isIPad) ? 1600 : 3000;
      if (cropWidth > SIZE_LIMIT || cropHeight > SIZE_LIMIT) {
        const ratio = cropWidth / cropHeight;
        if (ratio >= 1) {
          cropWidth = SIZE_LIMIT;
          cropHeight = Math.floor(SIZE_LIMIT / ratio);
        } else {
          cropHeight = SIZE_LIMIT;
          cropWidth = Math.floor(SIZE_LIMIT * ratio);
        }
      }

      canvas.width = cropWidth;
      canvas.height = cropHeight;

      context.drawImage(
        img,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        cropWidth,
        cropHeight
      );

      const processedImageData = context.getImageData(0, 0, cropWidth, cropHeight);
      try {
        const result = await processJimpImage(
          processedImageData,
          COLORS,
          numLayers,
          {
            despeckle,
            simplifyCurves,
            smoothness,
            colors,
            originalDimensions,
            useSuperpixels,
            useMorphology,
          }
        );
        resolve(result);
      } catch (error) {
        console.log(error);
        reject(error);
      }
    };
    img.onerror = () => reject(new Error('The image could not be loaded for tracing.'));
  });
};
